/* mypka-pdp-tabs.js — myPKA, 17.08.2026
 *
 * Macht aus den drei Inhaltsbloecken der Produktseite (Beschreibung,
 * Zutaten & Allergene, Naehrwerte & Inhalt) eine echte Auswahl: sichtbar ist
 * immer genau einer, Standard ist die Beschreibung.
 *
 * WARUM ZWEI DARSTELLUNGEN
 * Ab 751 px ist es ein Tab-Muster (tablist/tab/tabpanel, Pfeiltasten).
 * Darunter ein Akkordeon (button + aria-expanded + region). Grund ist
 * gemessen, nicht geraten: der Reiterstreifen ist 495 px breit. Bei 390 px
 * Viewport stehen 350 px zur Verfuegung, der dritte Reiter ist zu 17,6 %
 * sichtbar, bei 360 px zu 0,1 %, bei 320 px zu 0 %
 * (messdaten/m3-mobil-und-token.json). Ein Reiter, den man im Ruhezustand
 * nicht sieht, wird nicht geklickt — und "Naehrwerte & Inhalt" ist bei
 * Lebensmitteln die Angabe, die man am wenigsten verstecken darf. Das
 * Akkordeon war im Call vom 12.08. ausdruecklich als Alternative benannt.
 * Umschaltpunkt 750 px, weil das Theme selbst dort umschaltet
 * (base.css:6263, scroll-margin-top der gleichen drei Bloecke).
 *
 * ZUSTAND STECKT IM MARKUP, NICHT IM SKRIPT
 * Das inaktive Panel traegt `hidden` (= display:none). Der Inhalt ist damit
 * fuer Auge und Screenreader gleichermassen weg. Ein visibility- oder
 * opacity-Trick wuerde ihn vorlesbar stehen lassen, waehrend er visuell
 * verschwunden ist — genau die Falle, die hier vermieden werden soll.
 * Ohne JavaScript passiert nichts von alldem: dann greift die Regel in
 * mypka-pdp-tabs.css, die unter html.no-js alle Panels wieder aufdeckt, und
 * die Reiter sind wieder die Ankerlinks, die sie im Markup sind.
 *
 * ABGRENZUNG ZU lava-lamp.js
 * Der Streifen gehoert weiter lava-lamp.js (Pille, Klick, Randschatten).
 * Dieses Skript schaltet nur die Panels. lava-lamp.js erkennt Panel-Reiter am
 * Attribut data-mypka-pdp-reiter und scrollt bei ihnen nicht mehr.
 *
 * NACHTRAG 28.08.2026 (Felix) — Ticket 869eqz4bp, Audit-Befund M9:
 * MOBIL SCHLIESST EIN ZWEITER TIPP DEN OFFENEN BLOCK.
 *
 * Das ist KEINE Aufweichung der Festlegung von oben, sondern ihre Fortsetzung
 * in der zweiten Darstellung. Die Festlegung "genau ein Block sichtbar" gilt
 * unveraendert fuer das REITERMUSTER ab 751 px: dort ist "gar nichts
 * ausgewaehlt" ein Zustand, den es in einem Tab-Muster nicht geben darf, und
 * ein Klick auf den aktiven Reiter schliesst weiterhin nicht.
 *
 * Mobil ist es aber kein Reitermuster, sondern ein Akkordeon — und ein
 * Akkordeon, das sich nicht schliesst, tut auf den Tipp hin nichts. Gemessen
 * am 28.08.2026 bei 390 px, jeder Kopf zweimal geklickt: aria-expanded blieb
 * "true", die Panelhoehe blieb 941 px. Der Kunde steht vor 941 px Fliesstext,
 * tippt auf die Ueberschrift und die Seite ruehrt sich nicht.
 *
 * Der Zustand "alles zu" ist damit mobil erreichbar. Beim Wechsel zurueck auf
 * Desktopbreite wird er wieder aufgeloest (siehe `beiWechsel`) — sonst haette
 * das Reitermuster nach dem Drehen des Geraets kein ausgewaehltes Panel.
 */
(() => {
	const UMSCHALTPUNKT = 750;
	const WRAPPER = ".main-product__tabs-blocks--wrapper";

	const aufbauen = (wurzel) => {
		const wrapper = (wurzel || document).querySelector(WRAPPER);
		if (!wrapper) return null;

		const eintraege = [...wrapper.querySelectorAll("[data-mypka-pdp-block]")]
			.map((huelle) => {
				const id = huelle.dataset.mypkaPdpBlock;
				return {
					id,
					huelle,
					panel: huelle.querySelector(`#${CSS.escape(id)}`),
					schalter: huelle.querySelector(`[data-mypka-pdp-schalter="${id}"]`),
					reiter: document.querySelector(`[data-mypka-pdp-reiter="${id}"]`),
				};
			})
			.filter((e) => e.panel && e.schalter);

		if (eintraege.length === 0) return null;

		// Nur Bloecke MIT Reiter nehmen am Tab-Muster teil. main-product.js
		// entfernt Reiter, deren Sektion fehlt (setTabsText) — ein Block ohne
		// Reiter bliebe sonst am Desktop unerreichbar verborgen.
		const schaltbar = eintraege.filter((e) => e.reiter);
		eintraege
			.filter((e) => !e.reiter)
			.forEach((e) => {
				e.panel.hidden = false;
				e.schalter.setAttribute("aria-expanded", "true");
			});

		return { wrapper, eintraege, schaltbar };
	};

	const istMobil = () =>
		window.matchMedia(`(max-width: ${UMSCHALTPUNKT}px)`).matches;

	const starte = (wurzel) => {
		const bau = aufbauen(wurzel);
		if (!bau) return;
		const { schaltbar } = bau;
		if (schaltbar.length === 0) return;

		let mobil = istMobil();

		/* Die Rollen wechseln mit dem Umschaltpunkt.
		 * Desktop: tabpanel, beschriftet vom Reiter.
		 * Mobil:   region, beschriftet vom Akkordeon-Schalter. Der Reiter ist
		 *          dort display:none; ein aria-labelledby auf ein unsichtbares
		 *          Element ergaebe keinen zugaenglichen Namen. */
		const rollenSetzen = () => {
			schaltbar.forEach((e) => {
				if (mobil) {
					e.panel.setAttribute("role", "region");
					e.panel.setAttribute("aria-labelledby", e.schalter.id);
					e.panel.removeAttribute("tabindex");
				} else {
					e.panel.setAttribute("role", "tabpanel");
					e.panel.setAttribute("aria-labelledby", e.reiter.id);
					// Das Zutaten-Panel enthaelt kein fokussierbares Element
					// (gemessen: 0). Ohne tabindex koennte man es per Tastatur
					// weder erreichen noch scrollen.
					e.panel.setAttribute("tabindex", "0");
				}

				/* Die Entdoppelung der Ueberschrift stand bis zum 25.08.2026 hier
				 * und ist nach Liquid gewandert (snippets/section-header.liquid
				 * plus die vier Panel-Snippets). Zwei Gruende:
				 *
				 *   1. Sie galt hier nur MOBIL. Am Desktop stand die Zeile doppelt —
				 *      genau der Befund von Erdem am 25.08.2026.
				 *   2. Sie hat `hidden` gesetzt, also display:none. Damit war die
				 *      Ueberschrift auch fuer Screenreader weg. Gewollt ist das
				 *      Gegenteil: unsichtbar, aber in der Gliederung. Das leistet
				 *      `visually-hidden`, und das entscheidet jetzt der Server —
				 *      vor dem ersten Paint, ohne Aufblitzen und ohne JS-Abhaengigkeit.
				 *
				 * Wer hier wieder eine Titel-Logik einbaut, hat zwei Quellen fuer
				 * dieselbe Entscheidung. Der Textvergleich lebt in Liquid. */
			});
		};

		// Zuletzt geoeffneter Block. Wird nur gebraucht, um aus dem mobilen
		// Zustand "alles zu" heraus wieder ein Panel zu waehlen, wenn die
		// Breite auf das Reitermuster wechselt.
		let zuletzt = null;

		const aktiviere = (id, { fokus = false } = {}) => {
			schaltbar.forEach((e) => {
				const aktiv = e.id === id;
				e.panel.hidden = !aktiv;
				e.schalter.setAttribute("aria-expanded", aktiv ? "true" : "false");
				e.reiter.setAttribute("aria-selected", aktiv ? "true" : "false");
				// Roving tabindex: nur der gewaehlte Reiter liegt im Tab-Lauf,
				// innerhalb der Leiste navigiert man mit den Pfeiltasten.
				e.reiter.setAttribute("tabindex", aktiv ? "0" : "-1");
				const li = e.reiter.closest("li");
				if (li) li.classList.toggle("active", aktiv);
			});
			if (fokus) {
				const ziel = schaltbar.find((e) => e.id === id);
				if (ziel) (mobil ? ziel.schalter : ziel.reiter).focus();
			}
			zuletzt = id;
		};

		/* Nur mobil erreichbar (Ticket 869eqz4bp). Schliesst GENAU den einen
		 * Block und laesst alles andere in Ruhe.
		 *
		 * Der Roving-tabindex wird hier ABSICHTLICH nicht angefasst: der
		 * geschlossene Reiter behaelt tabindex="0" und bleibt damit der eine
		 * Einstieg der Leiste in den Tab-Lauf. Wuerde er auf -1 gesetzt, waere
		 * die Reiterleiste nach dem Zuklappen per Tastatur gar nicht mehr
		 * erreichbar — ein Fehler, den man erst beim Wechsel auf Desktopbreite
		 * saehe, weil die Leiste mobil display:none ist. */
		const schliesse = (id) => {
			const e = schaltbar.find((x) => x.id === id);
			if (!e) return;
			e.panel.hidden = true;
			e.schalter.setAttribute("aria-expanded", "false");
			e.reiter.setAttribute("aria-selected", "false");
			const li = e.reiter.closest("li");
			if (li) li.classList.remove("active");
		};

		// --- Reiter: Klick + Tastatur -------------------------------------
		schaltbar.forEach((e, i) => {
			e.reiter.addEventListener("click", (ev) => {
				ev.preventDefault();
				aktiviere(e.id);
			});

			e.reiter.addEventListener("keydown", (ev) => {
				const letzte = schaltbar.length - 1;
				let neu = null;
				switch (ev.key) {
					case "ArrowRight":
						neu = i === letzte ? 0 : i + 1;
						break;
					case "ArrowLeft":
						neu = i === 0 ? letzte : i - 1;
						break;
					case "Home":
						neu = 0;
						break;
					case "End":
						neu = letzte;
						break;
					case " ":
					case "Spacebar":
						// Enter loest am <a> von selbst ein click aus, die
						// Leertaste nicht — als role="tab" muss sie es aber.
						ev.preventDefault();
						aktiviere(e.id);
						return;
					default:
						return;
				}
				ev.preventDefault();
				aktiviere(schaltbar[neu].id, { fokus: true });
			});
		});

		// --- Akkordeon-Schalter -------------------------------------------
		schaltbar.forEach((e) => {
			e.schalter.addEventListener("click", () => {
				// Ab 751 px unveraendert: genau ein Block sichtbar, ein Klick auf
				// den aktiven Reiter schliesst NICHT.
				// Mobil (Ticket 869eqz4bp): ein zweiter Tipp auf einen offenen
				// Kopf schliesst ihn. Begruendung im Kopfkommentar der Datei.
				if (mobil && e.schalter.getAttribute("aria-expanded") === "true") {
					schliesse(e.id);
					return;
				}
				aktiviere(e.id);
			});
		});

		// --- Umschaltpunkt ------------------------------------------------
		const mql = window.matchMedia(`(max-width: ${UMSCHALTPUNKT}px)`);
		const beiWechsel = () => {
			mobil = mql.matches;
			rollenSetzen();
			/* Ticket 869eqz4bp: "alles zu" gibt es nur mobil. Wechselt die
			 * Breite auf das Reitermuster, waere dort sonst kein Panel gewaehlt —
			 * ein Zustand, den das Tab-Muster nicht kennt. Diese Zeile laeuft
			 * ausschliesslich bei einem Breitenwechsel und nur dann, wenn der
			 * mobile Zweig ihn erzeugt hat; eine Sitzung, die am Desktop
			 * beginnt, erreicht sie nie. */
			if (!mobil && !schaltbar.some((e) => !e.panel.hidden)) {
				aktiviere(zuletzt || schaltbar[0].id);
			}
		};
		if (mql.addEventListener) mql.addEventListener("change", beiWechsel);
		else mql.addListener(beiWechsel);

		// --- Startzustand -------------------------------------------------
		// Reihenfolge: Direktlink (/products/x#specs_long) schlaegt Server-
		// Vorgabe schlaegt erster Block.
		const ausHash = decodeURIComponent((location.hash || "").slice(1));
		const perHash = schaltbar.find((e) => e.id === ausHash);
		const vomServer = schaltbar.find((e) => !e.panel.hidden);
		rollenSetzen();
		aktiviere((perHash || vomServer || schaltbar[0]).id);
	};

	const einmal = () => starte(document);

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", einmal);
	} else {
		einmal();
	}

	// Theme-Editor: Sektion wird neu gerendert, Zustand neu aufsetzen.
	document.addEventListener("shopify:section:load", (event) => {
		starte(event.target);
	});
})();
